import type { Salarie } from '@shared/types/Salarie';
import type { SaisieBulletin } from './SaisieBulletin';
import { joursPayesDansMois } from './ProrataPaie';
import { tauxPrimeAnciennete } from './PrimeAnciennete';
import { roundFCFA } from '@shared/utils/round';
import {
  HEURES_MENSUELLES_LEGAL,
  JOURS_MOIS_PAIE,
  SMIG,
  TAUX_HS_110,
  TAUX_HS_125,
  TAUX_HS_150,
  TAUX_HS_200,
} from '@shared/constants/Taux';

/**
 * Ligne de la grille « Saisie mensuelle » : ce que l'utilisateur tape,
 * avant conversion en montants pour le moteur de paie.
 */
export interface LigneSaisieMensuelle {
  salarie_id: string;
  matricule: string;
  salaire_base: number;
  hs_110: number;
  hs_125: number;
  hs_150: number;
  hs_200: number;
  prime: number;
  prime_extra: number;
  motif_prime_extra: string;
  absences_jours: number;
  avance: number;
  acompte: number;
  quinzaine: number;
  pret: number;
  /** A-06 : statut de validation RH (persisté avec la saisie). */
  validation_rh?: 'ok' | 'a_controler' | 'approuve' | 'refuse';
}

export interface OptionsSaisieMensuelle {
  periode: string;
  inclure13e: boolean;
}

export const CHAMPS_NUMERIQUES = [
  'salaire_base',
  'hs_110',
  'hs_125',
  'hs_150',
  'hs_200',
  'prime',
  'prime_extra',
  'absences_jours',
  'avance',
  'acompte',
  'quinzaine',
  'pret',
] as const;

export type ChampNumeriqueSaisie = (typeof CHAMPS_NUMERIQUES)[number];

const COLONNES_CSV = [
  'Matricule',
  'Salaire base',
  'HS 110 %',
  'HS 125 %',
  'HS 150 %',
  'HS 200 %',
  'Prime',
  'Prime extra.',
  'Motif prime extra.',
  'Absences (j)',
  'Avance',
  'Acompte',
  'Quinzaine',
  'Prêt',
] as const;

export const SAISIE_CSV_HEADER = COLONNES_CSV.join(';');

/** Modèle CSV aligné sur l'import (matricule existant requis). */
export function saisieCsvTemplate(): string {
  const example = ['M0001', 150_000, 0, 0, 0, 0, 0, 0, '', 0, 0, 0, 0, 0].join(';');
  return `${SAISIE_CSV_HEADER}\n${example}`;
}

/** Ligne vide prête à afficher, préremplie avec le salaire de la fiche. */
export function ligneVide(salarie: Salarie): LigneSaisieMensuelle {
  return {
    salarie_id: salarie.id,
    matricule: salarie.matricule,
    salaire_base: salarie.salaire_base,
    hs_110: 0,
    hs_125: 0,
    hs_150: 0,
    hs_200: 0,
    prime: 0,
    prime_extra: 0,
    motif_prime_extra: '',
    absences_jours: 0,
    avance: 0,
    acompte: 0,
    quinzaine: 0,
    pret: 0,
  };
}

export function fusionnerLigne(
  salarie: Salarie,
  partielle: Partial<LigneSaisieMensuelle> | undefined,
): LigneSaisieMensuelle {
  return {
    ...ligneVide(salarie),
    ...partielle,
    salarie_id: salarie.id,
    matricule: salarie.matricule,
  };
}

/** Années révolues à la fin de la période de paie. */
export function ancienneteAnnees(dateEmbauche: string, periode: string): number {
  const [annee, mois] = periode.split('-').map(Number);
  if (!annee || !mois) return 0;
  const fin = new Date(annee, mois, 0);
  const debut = new Date(dateEmbauche);
  if (Number.isNaN(debut.getTime()) || Number.isNaN(fin.getTime())) return 0;
  const moisEcoules =
    (fin.getFullYear() - debut.getFullYear()) * 12 + (fin.getMonth() - debut.getMonth());
  return Math.max(0, Math.floor(moisEcoules / 12));
}

/**
 * Prime d'ancienneté du barème général : 2 % à 2 ans, puis +1 % par an, plafonné à 30 %.
 * Même barème que le solde de tout compte et que le résumé de convention affiché
 * dans l'écran Entreprise : une seule formule pour toute l'application.
 */
export function primeAnciennete(
  salaireBase: number,
  dateEmbauche: string,
  periode: string,
): number {
  const ans = ancienneteAnnees(dateEmbauche, periode);
  return roundFCFA(salaireBase * tauxPrimeAnciennete(ans));
}

/** Coefficients appliqués au taux horaire pour chacune des quatre colonnes de saisie. */
export interface CoefficientsHeuresSup {
  hs_110: number;
  hs_125: number;
  hs_150: number;
  hs_200: number;
}

/**
 * Valorisation des heures supplémentaires au taux horaire du mois.
 *
 * Sans `coefficients`, les colonnes valent 1,10 / 1,25 / 1,50 / 2,00 : ce ne sont PAS des taux légaux
 * (le Code du travail, art. 196, renvoie aux conventions) ; voir `MajorationsHeuresSup.ts` pour les
 * taux de chaque convention.
 */
export function montantHeuresSupplementaires(
  salaireBase: number,
  hs: Pick<LigneSaisieMensuelle, 'hs_110' | 'hs_125' | 'hs_150' | 'hs_200'>,
  coefficients: CoefficientsHeuresSup = {
    hs_110: TAUX_HS_110,
    hs_125: TAUX_HS_125,
    hs_150: TAUX_HS_150,
    hs_200: TAUX_HS_200,
  },
): number {
  const horaire = salaireBase / HEURES_MENSUELLES_LEGAL;
  return roundFCFA(
    horaire *
      (hs.hs_110 * coefficients.hs_110 +
        hs.hs_125 * coefficients.hs_125 +
        hs.hs_150 * coefficients.hs_150 +
        hs.hs_200 * coefficients.hs_200),
  );
}

export function totalRetenues(ligne: LigneSaisieMensuelle): number {
  return roundFCFA(ligne.avance + ligne.acompte + ligne.quinzaine + ligne.pret);
}

/** Un override de salaire ou une prime extraordinaire doit être signalé à la RH. */
export function exigeValidationRh(ligne: LigneSaisieMensuelle, salaireFiche: number): boolean {
  return ligne.salaire_base !== salaireFiche || ligne.prime_extra > 0;
}

/**
 * Le SMIG s'apprécie sur le salaire contractuel d'un mois plein : un mois incomplet
 * (embauche ou sortie) est proratisé plus tard, sans être refusé ici.
 */
export function validerLigne(ligne: LigneSaisieMensuelle): string | null {
  if (!Number.isFinite(ligne.salaire_base) || ligne.salaire_base < SMIG) {
    return `Salaire de base inférieur au SMIG (${SMIG} FCFA)`;
  }
  if (!Number.isInteger(ligne.salaire_base)) {
    return 'Le salaire de base doit être un entier en FCFA';
  }
  if (ligne.absences_jours < 0 || ligne.absences_jours > JOURS_MOIS_PAIE) {
    return `Absences hors plage 0–${JOURS_MOIS_PAIE} jours`;
  }
  for (const champ of CHAMPS_NUMERIQUES) {
    const valeur = ligne[champ];
    if (!Number.isFinite(valeur) || valeur < 0) {
      return `Valeur négative ou invalide (${champ})`;
    }
  }
  if (ligne.prime_extra > 0 && !ligne.motif_prime_extra.trim()) {
    return 'Motif obligatoire dès qu’une prime extraordinaire est saisie';
  }
  return null;
}

/**
 * Convertit la grille en saisie attendue par `calculerBulletin`.
 *
 * Le salaire du mois est d'abord proratisé en trentièmes pour une embauche ou une
 * sortie en cours de mois, puis réduit par les absences saisies. Prime d'ancienneté
 * et 13ᵉ mois suivent ce salaire réellement dû. Les heures supplémentaires restent
 * valorisées au taux horaire contractuel : elles rémunèrent des heures effectuées.
 * Les retenues (avance, acompte, quinzaine, prêt) sont regroupées sur `avance`.
 */
export function versSaisieBulletin(
  salarie: Salarie,
  ligne: LigneSaisieMensuelle,
  options: OptionsSaisieMensuelle & { avantage_nature?: number },
): SaisieBulletin {
  const erreur = validerLigne(ligne);
  if (erreur) throw new Error(`${salarie.matricule} : ${erreur}`);

  const joursPayes = joursPayesDansMois(
    options.periode,
    salarie.date_embauche,
    salarie.date_sortie,
  );
  const absences = Math.min(ligne.absences_jours, JOURS_MOIS_PAIE);
  /** Ratio trentièmes (embauche/sortie) × absences — appliqué au salaire ET aux indemnités. */
  const ratioPresence =
    (joursPayes / JOURS_MOIS_PAIE) * ((JOURS_MOIS_PAIE - absences) / JOURS_MOIS_PAIE);
  const prorata = (montant: number): number => roundFCFA(montant * ratioPresence);

  const brutApresAbsences = prorata(ligne.salaire_base);

  return {
    salaire_base: brutApresAbsences,
    sursalaire: prorata(salarie.sursalaire ?? 0),
    prime: roundFCFA(ligne.prime),
    prime_extra: roundFCFA(ligne.prime_extra),
    prime_anciennete: primeAnciennete(brutApresAbsences, salarie.date_embauche, options.periode),
    treizieme_mois: options.inclure13e ? brutApresAbsences : 0,
    hsup_total: montantHeuresSupplementaires(ligne.salaire_base, ligne),
    transport: prorata(salarie.indemnite_transport ?? 0),
    vehicule: prorata(salarie.indemnite_vehicule ?? 0),
    logement: prorata(salarie.indemnite_logement ?? 0),
    fonction_ind: prorata(salarie.indemnite_fonction ?? 0),
    autres: prorata(salarie.indemnite_autres ?? 0),
    avance: totalRetenues(ligne),
    // A-18 : avantages en nature (immos) — aussi au prorata des jours payés
    avantage_nature: prorata(options.avantage_nature ?? 0),
    jours_travailles: joursPayes,
  };
}

export function exporterCsv(lignes: readonly LigneSaisieMensuelle[]): string {
  const corps = lignes.map((l) =>
    [
      echapper(l.matricule),
      l.salaire_base,
      l.hs_110,
      l.hs_125,
      l.hs_150,
      l.hs_200,
      l.prime,
      l.prime_extra,
      echapper(l.motif_prime_extra),
      l.absences_jours,
      l.avance,
      l.acompte,
      l.quinzaine,
      l.pret,
    ].join(';'),
  );
  return [COLONNES_CSV.join(';'), ...corps].join('\n');
}

/** Importe un CSV nominatif. Les lignes dont le matricule est inconnu sont ignorées. */
export function importerCsv(
  csv: string,
  salaries: readonly Salarie[],
): { lignes: LigneSaisieMensuelle[]; ignorees: number } {
  const parMatricule = new Map(salaries.map((s) => [s.matricule.trim().toLowerCase(), s]));
  const lignesFichier = csv
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .filter((l) => l.trim());
  if (lignesFichier.length === 0) return { lignes: [], ignorees: 0 };

  const debut = estEntete(lignesFichier[0]!) ? 1 : 0;
  const lignes: LigneSaisieMensuelle[] = [];
  let ignorees = 0;
  for (let i = debut; i < lignesFichier.length; i += 1) {
    const cellules = decouperCsv(lignesFichier[i]!);
    const matricule = (cellules[0] ?? '').trim();
    const salarie = parMatricule.get(matricule.toLowerCase());
    if (!salarie) {
      ignorees += 1;
      continue;
    }
    const nombre = (index: number): number => {
      const brut = (cellules[index] ?? '').replace(/\s/g, '').replace(',', '.');
      const n = Number(brut);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    };
    lignes.push({
      ...ligneVide(salarie),
      salaire_base: nombre(1) || salarie.salaire_base,
      hs_110: nombre(2),
      hs_125: nombre(3),
      hs_150: nombre(4),
      hs_200: nombre(5),
      prime: nombre(6),
      prime_extra: nombre(7),
      motif_prime_extra: cellules[8] ?? '',
      absences_jours: nombre(9),
      avance: nombre(10),
      acompte: nombre(11),
      quinzaine: nombre(12),
      pret: nombre(13),
    });
  }
  return { lignes, ignorees };
}

function estEntete(ligne: string): boolean {
  return ligne.toLowerCase().includes('matricule');
}

function echapper(value: string): string {
  return /[;"\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function decouperCsv(ligne: string): string[] {
  const cellules: string[] = [];
  let courant = '';
  let dansGuillemets = false;
  for (let i = 0; i < ligne.length; i += 1) {
    const c = ligne[i]!;
    if (dansGuillemets) {
      if (c === '"' && ligne[i + 1] === '"') {
        courant += '"';
        i += 1;
      } else if (c === '"') {
        dansGuillemets = false;
      } else {
        courant += c;
      }
    } else if (c === '"') {
      dansGuillemets = true;
    } else if (c === ';') {
      cellules.push(courant);
      courant = '';
    } else {
      courant += c;
    }
  }
  cellules.push(courant);
  return cellules;
}
